/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2024 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import ErrorBoundary from 'components/detail/common/ErrorBoundary';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getFair, getFairs, getMachine, getMachines, getMachinesByFair } from '../../redux/selectors';
import TvViewDetail from './tvViewDetail';
import TvViewMachineGrid from './tvViewMachineGrid';
import TvViewMapList from './tvViewMapList';
import { IStore } from 'redux/storeType';
import { IsLandscape } from 'utilities/Orientation';
import ENVs from 'utilities/ENVs';

const SlideTypes = {
  MapList: 0,
  MachineGrid: 1,
  MachineDetail: 2,
  ImgLogo: 3,
  ImgNumbers: 4,
  ImgApp: 5,
  Video: 6,
  ImgDaily: 7
};

function MapListWrapper(props) {
  // TODO filter necessary properties
  return <TvViewMapList {...props} />;
}

function MachineGridWrapper(props) {
  return <TvViewMachineGrid machines={props.machinesOnFair} />;
}

function MachineDetailWrapper(props) {
  if (props.machinesOnFair.length) {
    const machine = props.match.params.machine ? props.machine : props.machinesOnFair[props.subIteration % props.machinesOnFair.length];
    return <TvViewDetail {...props} machine={machine} />;
  }
  return <p>No Machines on the fair.</p>;
}

function ImgSelector({ landscape, portrait, alt }: { landscape: string, portrait: string, alt: string }) {
  const [isLandscape, setIsLandscape] = useState<boolean>(IsLandscape());
  useEffect(
    () => {
      const handOrientationChange = () => {
        setIsLandscape(IsLandscape());
      };
      window.addEventListener('resize', handOrientationChange);
      return () => {
        window.removeEventListener('resize', handOrientationChange);
      };
    },
    []
  );

  if (isLandscape) {
    return <img alt={alt} className="fullscreen" src={landscape} />;
  } else {
    return <img alt={alt} className="fullscreen" src={portrait} />;
  }
}

function ImgLogoWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/1_logo_HD.jpg`;
  const portraitImg = `/watermarked/TvView/${props.match.params.fair}/1_logo_HD_P.jpg`;
  return <ImgSelector alt="showcase logo" landscape={landscapeImg} portrait={portraitImg} />;
}

function ImgNumbersWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/2_zahlen_HD.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/2_zahlen_HD_P.jpg`;
  return <ImgSelector alt="showcase numbers" landscape={landscapeImg} portrait={portraitImg} />;
}

function ImgAppWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/3_app_HD.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/3_app_HD_P.jpg`;
  return <ImgSelector alt="showcase app" landscape={landscapeImg} portrait={portraitImg} />;
}

function VideoWrapper(props) {
  /// Chrome does not allow autoplay without muted
  return <video src={`${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/` + props.match.params.fair + '/umati.webm'} className="fullscreen fullscreen-width" autoPlay={true} />;
}

function ImgDailyWrapper(props) {
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekday = weekdays[(new Date()).getDay()];
  const landscapeImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/today_${weekday}.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl()}/watermarked/TvView/${props.match.params.fair}/today_${weekday}_P.jpg`;
  return <ImgSelector alt="showcase daily" landscape={landscapeImg} portrait={portraitImg} />;
}

const slides = [
  MapListWrapper,
  MachineGridWrapper,
  MachineDetailWrapper,
  ImgLogoWrapper,
  ImgNumbersWrapper,
  ImgAppWrapper,
  VideoWrapper,
  ImgDailyWrapper
];

const slideOrder = [
  { slideType: SlideTypes.MapList, durationInSec: 8 },
  { slideType: SlideTypes.MachineGrid, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.ImgLogo, durationInSec: 8 },
  { slideType: SlideTypes.ImgNumbers, durationInSec: 8 },
  { slideType: SlideTypes.ImgApp, durationInSec: 8 },
  { slideType: SlideTypes.Video, durationInSec: 7 },
  { slideType: SlideTypes.ImgDaily, durationInSec: 8 }
];

class StateCalculator {
  numPerElement: { [key: string]: number };
  offset: number[];
  subOffset: number[];
  totalOffset: number;

  constructor() {
    this.numPerElement = {};
    this.offset = [];
    this.subOffset = [];
    this.totalOffset = 0;
    for (const el of slideOrder) {
      if (!this.numPerElement[el.slideType]) {
        this.numPerElement[el.slideType] = 0;
      }
      this.subOffset.push(this.numPerElement[el.slideType]);
      ++this.numPerElement[el.slideType];
      this.totalOffset += el.durationInSec;
      this.offset.push(this.totalOffset);
    }
  }

  GetState(num: number) {
    num = Math.floor(num);
    const bounded = num % this.totalOffset;
    let iSlide = -1;
    for (const [iOffset, offset] of this.offset.entries()) {
      if (bounded < offset) {
        iSlide = iOffset;
        break;
      }
    }
    if (iSlide === -1) {
      /// Should never happen
      throw new Error('Corrupted data structures.');
    }
    const slideDefinition = slideOrder[iSlide];
    const iteration = Math.floor(num / this.totalOffset);
    const subIteration = iteration * this.numPerElement[slideDefinition.slideType] + this.subOffset[iSlide];

    return {
      slideType: slideDefinition.slideType,
      subIteration
    };
  }

  GetStateFromTime() {
    const number = Math.floor((new Date()).getTime() / 1000);
    return this.GetState(number);
  }
}

type ITvViewParams = {
  fair: string,
  slide?: string,
  machine?: string
}

const stateCalculator = new StateCalculator();
function TvView() {
  const [state, setState] = useState(
    stateCalculator.GetStateFromTime()
  );
  let CurrentSlide = slides[state.slideType];
  const params = useParams() as ITvViewParams;
  const props = {
    machines: useSelector((state: IStore) => getMachines(state)),
    machinesOnFair: useSelector((state: IStore) => getMachinesByFair(state, params.fair)),
    fairs: useSelector((state: IStore) => getFairs(state)),
    fair: useSelector((state: IStore) => getFair(state, params.fair)),
    machine: useSelector((state: IStore) => getMachine(state, params.machine))
  };

  if (params.slide) {
    CurrentSlide = slides[parseInt(params.slide) % slides.length];
  } else {
    /* eslint-disable */
    useEffect(
      () => {
        const timerId = setInterval(() => {
          setState(stateCalculator.GetStateFromTime());
        }, 250); // Check every 250ms for a new state. There is no reRender, if the result is the same.

        return () => clearInterval(timerId);
      }
    );
    /* eslint-enable */
  }

  return (
    <div className="tv-wrapper">
      <div className="tv-slide">
        <ErrorBoundary state={`${params.slide}-${state.subIteration}`}>
          <CurrentSlide match={{ params }} {...props} subIteration={state.subIteration} />
        </ErrorBoundary>
      </div>
    </div>
  );
}

export default TvView;
